import React, {
  useMemo,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import EditableWidget, { WidgetSettingsPortal } from "./EditableWidget";
import ViewWidget from "./ViewWidget";
import { useMobileWidgetEditor } from "./MobileWidgetEditor";
import {
  useDragAndDrop,
  compactLayoutVertically,
} from "../../hooks/useDragAndDrop";
import {
  fixAllLayouts,
  compactAndFixLayout,
  hasLayoutOverlaps,
} from "../../utils/layoutUtils";
import { useLayoutFixer } from "../../hooks/useLayoutFixer";
import { useMasonryLayout } from "../../hooks/useMasonryLayout";
import {
  useResponsiveLayout,
  BREAKPOINTS,
} from "../../hooks/useResponsiveLayout";
import { useResizePreview } from "../../hooks/useResizePreview";
import { sortWidgetsByPosition } from "@/utils/widgetUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";

// Define preview mode specific settings
const PREVIEW_SETTINGS = {
  desktop: {
    totalColumns: 12,
    maxWidth: "max-w-7xl", // ~1280px
    containerClass: "w-full max-w-7xl", // Add max-width constraint
  },
  tablet: {
    totalColumns: 8,
    maxWidth: "max-w-3xl", // ~768px
    containerClass: "w-[768px] max-w-full", // Fixed width with max-width safety
  },
  mobile: {
    totalColumns: 4,
    maxWidth: "max-w-sm", // ~480px
    containerClass: "w-[375px] max-w-full", // Accurate mobile width with centering
  },
};

const GAP_PIXELS = 24;

const MasonryLayout = forwardRef(
  (
    {
      widgets,
      isEditing,
      spaceId,
      spaceSlug,
      onSaveStatusChange,
      onDelete,
      spaceData,
      handleWidgetUpdate,
      previewMode,
      isLightBackground = false,
      activeGalleryId,
      queueAction,
      onMobileWidgetSelect,
      widgetsFlyOff = false,
    },
    ref
  ) => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [widgetWithOpenSettings, setWidgetWithOpenSettings] = useState(null);

    // Mobile widget selection state
    const [selectedMobileWidgetId, setSelectedMobileWidgetId] = useState(null);

    // Notify parent of mobile widget selection changes
    useEffect(() => {
      if (onMobileWidgetSelect) {
        onMobileWidgetSelect(selectedMobileWidgetId);
      }
    }, [selectedMobileWidgetId, onMobileWidgetSelect]);

    useEffect(() => {
      const settingsPanel = document.querySelector("[data-settings-panel]");
      setIsSettingsOpen(!!settingsPanel);

      // Use MutationObserver to watch for settings panel being added/removed
      const observer = new MutationObserver((mutations) => {
        for (let mutation of mutations) {
          if (mutation.type === "childList") {
            setIsSettingsOpen(
              !!document.querySelector("[data-settings-panel]")
            );
          }
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => observer.disconnect();
    }, []);

    // Use the responsive layout hook - without autoResize
    const { activeLayout, windowWidth } = useResponsiveLayout(previewMode);

    // Apply automatic layout fixes for mobile/tablet
    const { fixedWidgets, hasAppliedFixes } = useLayoutFixer({
      widgets,
      activeLayout,
      handleWidgetUpdate,
      isEditing,
    });

    // Check if we're on a mobile device
    const isMobileDevice = useMemo(() => {
      if (typeof window === "undefined") return false;
      return windowWidth <= BREAKPOINTS.mobile;
    }, [windowWidth]);

    const getLastPosition = () => {
      return (
        Math.max(
          ...widgets.map(
            (widget) => widget.layout[activeLayout]?.position || 0
          ),
          -1
        ) + 1
      );
    };

    useEffect(() => {
      const items = widgets.map((widget) => ({
        id: widget.id,
        layout: widget.layout[activeLayout] || {},
      }));

      items.sort((a, b) => (a.layout.position || 0) - (b.layout.position || 0));

      // Collect all widgets that need position updates into a batch
      const updateBatch = [];

      items.forEach((item, index) => {
        if (item.layout.position === undefined) {
          updateBatch.push({
            id: item.id,
            layout: {
              [activeLayout]: {
                ...item.layout,
                position: getLastPosition(),
              },
            },
          });
        }
      });

      // If we have updates, handle them all at once
      if (updateBatch.length > 0) {
        handleWidgetUpdate(null, {
          type: "UPDATE_WIDGET",
          widgets: widgets.map((widget) => {
            const update = updateBatch.find((u) => u.id === widget.id);
            if (update) {
              return {
                ...widget,
                layout: {
                  ...widget.layout,
                  ...update.layout,
                },
              };
            }
            return widget;
          }),
          triggerAutosave: true,
        });
      }
    }, [widgets, activeLayout]);

    // Helper function to get a layout with fallbacks (mobile -> tablet -> desktop)
    const getLayoutWithFallback = (
      widget,
      layoutKey,
      property,
      defaultValue
    ) => {
      // First try the exact layout
      if (widget.layout[layoutKey]?.[property] !== undefined) {
        // Ensure startColumn is never negative if this is the property
        if (property === "startColumn") {
          return Math.max(0, widget.layout[layoutKey][property]);
        }
        return widget.layout[layoutKey][property];
      }

      // Fallback chain based on device size
      if (layoutKey === "mobile") {
        // Mobile falls back to tablet then desktop
        if (property === "startColumn") {
          return widget.layout.tablet?.[property] !== undefined
            ? Math.max(0, widget.layout.tablet[property])
            : widget.layout.desktop?.[property] !== undefined
              ? Math.max(0, widget.layout.desktop[property])
              : defaultValue;
        } else {
          return widget.layout.tablet?.[property] !== undefined
            ? widget.layout.tablet[property]
            : widget.layout.desktop?.[property] !== undefined
              ? widget.layout.desktop[property]
              : defaultValue;
        }
      } else if (layoutKey === "tablet") {
        // Tablet falls back to desktop
        if (property === "startColumn") {
          return widget.layout.desktop?.[property] !== undefined
            ? Math.max(0, widget.layout.desktop[property])
            : defaultValue;
        } else {
          return widget.layout.desktop?.[property] !== undefined
            ? widget.layout.desktop[property]
            : defaultValue;
        }
      }

      // Desktop is the base, just return default if not found
      // For startColumn, ensure it's never negative
      if (property === "startColumn") {
        return Math.max(0, defaultValue);
      }
      return defaultValue;
    };

    const items = useMemo(() => {
      // Use fixed widgets if available, otherwise use original widgets
      const widgetsToUse = fixedWidgets || widgets;

      // Sort widgets by position for the current active layout
      const sortedWidgets = sortWidgetsByPosition(widgetsToUse, activeLayout);

      return sortedWidgets.map((widget) => {
        // Get layout values with proper fallbacks
        const columns = getLayoutWithFallback(
          widget,
          activeLayout,
          "columns",
          activeLayout === "tablet"
            ? Math.min(6, widget.layout.desktop?.columns || 4) // Tablet uses max 6 columns
            : 4 // Default for desktop and mobile
        );

        const columnHeight = getLayoutWithFallback(
          widget,
          activeLayout,
          "columnHeight",
          columns // Default height is same as columns
        );

        const position = getLayoutWithFallback(
          widget,
          activeLayout,
          "position",
          0
        );
        const startColumn = getLayoutWithFallback(
          widget,
          activeLayout,
          "startColumn",
          0 // Default startColumn for all viewports
        );

        return {
          ...widget,
          columns,
          columnHeight,
          position,
          layout: {
            ...widget.layout,
            [activeLayout]: {
              ...widget.layout[activeLayout],
              columns,
              columnHeight,
              position,
              startColumn,
            },
          },
        };
      });
    }, [widgets, activeLayout, fixedWidgets]);

    // Initialize resize preview hook
    const {
      startResizePreview,
      stopResizePreview,
      updateResizePreview,
      getDisplacedPosition,
      getResizePreviewInfo,
      isWidgetBeingResized,
      isResizePreviewActive,
      isModifierPressed,
    } = useResizePreview({
      items,
      previewMode: activeLayout,
      totalColumns: PREVIEW_SETTINGS[activeLayout].totalColumns,
    });

    // Callbacks to manage widget settings - defined after items to avoid initialization error
    const handleOpenSettings = useCallback(
      (widgetId, settingsType) => {
        const widget = items.find((item) => item.id === widgetId);
        if (widget) {
          setWidgetWithOpenSettings({
            widget,
            settingsType,
          });
        }
      },
      [items]
    );

    const handleCloseSettings = useCallback(() => {
      setWidgetWithOpenSettings(null);
    }, []);

    const {
      positions,
      gridRef,
      measureRef,
      containerWidth,
      performMasonryLayout,
      measureItems,
      calculateWidgetDimensions,
      updateContainerHeight,
    } = useMasonryLayout({
      items,
      totalColumns: PREVIEW_SETTINGS[activeLayout].totalColumns,
      gapPixels: GAP_PIXELS,
      previewMode: activeLayout,
    });

    // Enhanced effect to handle window resize and orientation changes
    useEffect(() => {
      if (!gridRef.current) return;

      let resizeTimeout;
      let rafId;

      const handleResize = () => {
        // Clear any pending timeouts
        if (resizeTimeout) clearTimeout(resizeTimeout);
        if (rafId) cancelAnimationFrame(rafId);

        // Use requestAnimationFrame for smooth updates
        rafId = requestAnimationFrame(() => {
          // Debounce the actual layout recalculation
          resizeTimeout = setTimeout(() => {
            if (gridRef.current) {
              measureItems();
              performMasonryLayout();
              updateContainerHeight();
            }
          }, 150); // Slightly longer delay for better performance
        });
      };

      const handleOrientationChange = () => {
        // Handle device orientation changes with a longer delay
        // to ensure the viewport has fully adjusted
        setTimeout(() => {
          if (gridRef.current) {
            measureItems();
            performMasonryLayout();
            updateContainerHeight();
          }
        }, 300);
      };

      // Initial layout calculation when previewMode changes
      if (previewMode !== undefined) {
        handleResize();
      }

      // Set up event listeners for resize and orientation change
      window.addEventListener("resize", handleResize, { passive: true });
      window.addEventListener("orientationchange", handleOrientationChange, {
        passive: true,
      });

      // Also listen for visual viewport changes (mobile Safari address bar, etc.)
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", handleResize, {
          passive: true,
        });
      }

      return () => {
        // Cleanup
        if (resizeTimeout) clearTimeout(resizeTimeout);
        if (rafId) cancelAnimationFrame(rafId);

        window.removeEventListener("resize", handleResize);
        window.removeEventListener(
          "orientationchange",
          handleOrientationChange
        );

        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", handleResize);
        }
      };
    }, [
      previewMode,
      windowWidth,
      measureItems,
      performMasonryLayout,
      updateContainerHeight,
    ]);

    // Add a function to handle widget deletion with proper compaction and history tracking
    const handleWidgetDelete = useCallback(
      (widgetId) => {
        console.log(
          `🗑️ [MasonryLayout] Handling widget deletion for ${widgetId}`
        );

        // Deselect mobile widget if it's the one being deleted
        if (selectedMobileWidgetId === widgetId) {
          setSelectedMobileWidgetId(null);
        }

        // First, call the original onDelete handler which should handle history tracking
        if (onDelete) {
          onDelete(widgetId);

          // After deletion, compact the layout vertically
          setTimeout(() => {
            const remainingWidgets = widgets.filter(
              (widget) => widget.id !== widgetId
            );

            console.log(
              `🔄 [MasonryLayout] Compacting layout after deletion. Remaining widgets: ${remainingWidgets.length}`
            );

            const compactedLayout = compactLayoutVertically(
              remainingWidgets,
              activeLayout,
              PREVIEW_SETTINGS[activeLayout].totalColumns
            );

            // If compaction resulted in changes, update all widgets with new positions
            if (compactedLayout && compactedLayout.length > 0) {
              console.log(
                `✨ [MasonryLayout] Applying compacted layout with ${compactedLayout.length} widgets`
              );

              handleWidgetUpdate(null, {
                type: "UPDATE_WIDGET",
                widgets: compactedLayout,
                triggerAutosave: true,
                skipHistory: true, // Skip history for compaction to avoid cluttering undo/redo
              });

              // Recalculate layout after deletion and compaction
              setTimeout(() => {
                performMasonryLayout(containerWidth, compactedLayout);
                updateContainerHeight();
              }, 100);
            }
          }, 50);
        }
      },
      [
        onDelete,
        widgets,
        activeLayout,
        handleWidgetUpdate,
        containerWidth,
        performMasonryLayout,
        updateContainerHeight,
        selectedMobileWidgetId,
      ]
    );

    const {
      draggedItemId,
      dragPosition,
      potentialDropArea,
      isDropAnimating,
      handleMouseDown,
    } = useDragAndDrop({
      items,
      previewMode: activeLayout,
      handleWidgetUpdate,
      containerWidth,
      gapPixels: GAP_PIXELS,
      totalColumns: PREVIEW_SETTINGS[activeLayout].totalColumns,
      performMasonryLayout,
      isSettingsOpen,
      updateContainerHeight,
      isLightBackground,
    });

    // Mobile widget editor integration
    const mobileEditor = useMobileWidgetEditor({
      selectedWidgetId: selectedMobileWidgetId,
      onWidgetSelect: setSelectedMobileWidgetId,
      onWidgetDeselect: () => setSelectedMobileWidgetId(null),
      widgets: widgets, // Use raw widgets instead of processed items
      onWidgetUpdate: handleWidgetUpdate,
      onWidgetDelete: handleWidgetDelete, // Use the local handleWidgetDelete that includes compaction
      onOpenSettings: (widgetId, settingsType) => {
        const widget = items.find((item) => item.id === widgetId);
        if (widget) {
          setWidgetWithOpenSettings({ widget, settingsType });
        }
      },
      isMobileDevice,
      previewMode: activeLayout,
      containerWidth,
      performMasonryLayout,
      updateContainerHeight,
      isLightBackground,
      // Pass the main drag system
      dragSystem: {
        draggedItemId,
        dragPosition,
        potentialDropArea,
        isDropAnimating,
        handleMouseDown,
        isDragging: !!draggedItemId,
      },
    });

    // Expose recalculateLayout method through ref
    useImperativeHandle(ref, () => ({
      recalculateLayout: () => {
        measureItems();
        if (gridRef.current) {
          performMasonryLayout(gridRef.current.offsetWidth);
          updateContainerHeight();
        }
      },

      // Add a new method for handling startColumn changes
      recalculateAfterColumnChange: (widgetId) => {
        // Wait a moment to ensure all updates have been applied
        setTimeout(() => {
          // Get the widget that was changed
          const widget = items.find((item) => item.id === widgetId);
          if (!widget) return;

          // Force measurement of items
          measureItems();

          // Recalculate layout with the updated widget positions
          if (gridRef.current) {
            performMasonryLayout(gridRef.current.offsetWidth);
            updateContainerHeight();
          }
        }, 50);
      },
    }));

    // Calculate grid unit dimensions based on container width
    const calculateGridUnitDimensions = useCallback(() => {
      if (!containerWidth || containerWidth <= 0)
        return { columnWidth: 0, unitHeight: 0 };

      const totalColumns = PREVIEW_SETTINGS[activeLayout].totalColumns;
      if (!totalColumns || totalColumns <= 0)
        return { columnWidth: 0, unitHeight: 0 };

      const totalGapWidth = GAP_PIXELS * (totalColumns - 1);
      const usableWidth = containerWidth - totalGapWidth;

      // Ensure usableWidth is positive
      if (usableWidth <= 0) return { columnWidth: 0, unitHeight: 0 };

      const columnWidth = usableWidth / totalColumns;

      // Ensure columnWidth is positive and not NaN
      if (!columnWidth || columnWidth <= 0 || isNaN(columnWidth)) {
        return { columnWidth: 0, unitHeight: 0 };
      }

      const unitHeight = columnWidth; // Square units

      return { columnWidth, unitHeight };
    }, [containerWidth, activeLayout]);

    // Convert grid units to pixels for positioning
    const gridToPixels = useCallback(
      (startColumn, position, width, height) => {
        const { columnWidth, unitHeight } = calculateGridUnitDimensions();

        const left = startColumn * (columnWidth + GAP_PIXELS);
        const top = position * (unitHeight + GAP_PIXELS);
        const pixelWidth = width * columnWidth + (width - 1) * GAP_PIXELS;
        const pixelHeight = height * unitHeight + (height - 1) * GAP_PIXELS;

        return { left, top, width: pixelWidth, height: pixelHeight };
      },
      [calculateGridUnitDimensions]
    );

    // Calculate fly-off direction for each widget based on nearest screen edge
    const calculateFlyOffTransform = useCallback(
      (widgetId, position) => {
        if (!widgetsFlyOff || !containerWidth) return "";

        const { left, top, width, height } = position;
        const centerX = left + width / 2;
        const centerY = top + height / 2;

        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate distances to each edge from widget center
        const distanceToLeft = centerX;
        const distanceToRight = viewportWidth - centerX;
        const distanceToTop = centerY;
        const distanceToBottom = viewportHeight - centerY;

        // Find nearest edge
        const minDistance = Math.min(
          distanceToLeft,
          distanceToRight,
          distanceToTop,
          distanceToBottom
        );

        let flyOffX = 0;
        let flyOffY = 0;
        let rotation = 0;

        if (minDistance === distanceToLeft) {
          // Fly to left
          flyOffX = -(centerX + width);
          rotation = -15;
        } else if (minDistance === distanceToRight) {
          // Fly to right
          flyOffX = viewportWidth - centerX + width;
          rotation = 15;
        } else if (minDistance === distanceToTop) {
          // Fly to top
          flyOffY = -(centerY + height);
          rotation = Math.random() > 0.5 ? 10 : -10;
        } else {
          // Fly to bottom
          flyOffY = viewportHeight - centerY + height;
          rotation = Math.random() > 0.5 ? 10 : -10;
        }

        return `translate3d(${left + flyOffX}px, ${top + flyOffY}px, 0) rotate(${rotation}deg)`;
      },
      [widgetsFlyOff, containerWidth]
    );

    // Create animation style once on component mount
    useEffect(() => {
      // Check if style already exists
      if (!document.getElementById("drag-drop-animations")) {
        const style = document.createElement("style");
        style.id = "drag-drop-animations";
        style.innerHTML = `
          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.1); }
            70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
            100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
          }
          @keyframes pulse-drop-zone {
            0% { 
              box-shadow: 0 12px 30px rgba(96, 165, 250, 0.3), 0 0 0 1px rgba(96, 165, 250, 0.2);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 15px 40px rgba(96, 165, 250, 0.5), 0 0 0 2px rgba(96, 165, 250, 0.4);
              transform: scale(1.02);
            }
            100% { 
              box-shadow: 0 12px 30px rgba(96, 165, 250, 0.3), 0 0 0 1px rgba(96, 165, 250, 0.2);
              transform: scale(1);
            }
          }
        `;
        document.head.appendChild(style);
      }

      return () => {
        const existingStyle = document.getElementById("drag-drop-animations");
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }, []);

    // Function to get appropriate class for the preview container
    const getPreviewContainerClass = useCallback(() => {
      const baseClass = "relative transition-all duration-300 mx-auto";

      // Always use specific preview container classes regardless of editing mode
      const previewClass = PREVIEW_SETTINGS[activeLayout].containerClass;

      // Add border for tablet/mobile ONLY when in edit mode for preview purposes
      let deviceClass = "";
      if (isEditing) {
        if (activeLayout === "mobile") {
          deviceClass = "border-x border-gray-500/20 shadow-lg rounded-t-xl";
        } else if (activeLayout === "tablet") {
          deviceClass = "border-x border-gray-500/20 shadow-lg";
        }
      }

      return `${baseClass} ${previewClass} ${deviceClass}`;
    }, [activeLayout, isEditing]);

    // Inside the MasonryLayout component, after the gridRef is first used
    useEffect(() => {
      if (!gridRef.current) return;

      // Store a reference to this component on the DOM element
      // So child components can access layout recalculation methods
      gridRef.current.__ref = {
        recalculateLayout: () => {
          measureItems();
          performMasonryLayout(containerWidth);
          updateContainerHeight();
        },
      };

      // Listen for custom recalculation events from child components
      const handleRecalculate = () => {
        measureItems();
        performMasonryLayout(containerWidth);
        updateContainerHeight();
      };

      gridRef.current.addEventListener(
        "recalculate-masonry",
        handleRecalculate
      );

      return () => {
        if (gridRef.current) {
          gridRef.current.removeEventListener(
            "recalculate-masonry",
            handleRecalculate
          );
          delete gridRef.current.__ref;
        }
      };
    }, [
      gridRef,
      measureItems,
      performMasonryLayout,
      updateContainerHeight,
      containerWidth,
    ]);

    return (
      <div className="w-full flex justify-center px-4">
        <div
          ref={gridRef}
          data-masonry-container
          data-preview-mode={activeLayout}
          className={getPreviewContainerClass()}
          onTouchStart={
            isMobileDevice ? mobileEditor.handleContainerTap : undefined
          }
          style={{
            minHeight: "100px",
            height:
              Object.values(positions).length > 0
                ? Math.max(
                    ...Object.values(positions)
                      .filter(
                        (p) =>
                          p &&
                          typeof p.top === "number" &&
                          typeof p.height === "number"
                      )
                      .map((p) => p.top + p.height)
                  ) + "px"
                : "auto",
            paddingBottom: GAP_PIXELS + "px",
            background: "transparent", // Ensure container has transparent background
            boxShadow: "none", // Remove any shadow
            border: "none", // Override any border that might be set elsewhere
            position: "relative", // Ensure proper positioning
            zIndex: 1, // Ensure content is above background
            touchAction: "pan-y pinch-zoom", // Allow vertical scrolling but prevent horizontal interference
          }}
        >
          <div
            ref={measureRef}
            className="absolute invisible pointer-events-none"
            style={{ height: 0, overflow: "hidden" }}
          >
            {items.map((item) => {
              const { width, height } = calculateWidgetDimensions(item);
              // Ensure dimensions are valid numbers
              const safeWidth = isNaN(width) || width <= 0 ? 200 : width;
              const safeHeight = isNaN(height) || height <= 0 ? 200 : height;
              return (
                <div
                  key={item.id}
                  style={{ width: `${safeWidth}px`, height: `${safeHeight}px` }}
                >
                  {isEditing ? (
                    <EditableWidget
                      id={item.id}
                      widget={item}
                      layout={item.layout}
                      config={item.config}
                      isEditing={isEditing}
                      spaceId={spaceId}
                      spaceSlug={spaceSlug}
                      onSaveStatusChange={onSaveStatusChange}
                      handleWidgetUpdate={handleWidgetUpdate}
                      onDelete={handleWidgetDelete}
                      spaceData={spaceData}
                      previewMode={activeLayout}
                      totalColumns={PREVIEW_SETTINGS[activeLayout].totalColumns}
                      handleMouseDown={handleMouseDown}
                      allWidgets={items}
                      queueAction={queueAction}
                      onOpenSettings={handleOpenSettings}
                      onCloseSettings={handleCloseSettings}
                      // Mobile widget selection props
                      selectedMobileWidgetId={selectedMobileWidgetId}
                      isMobileDevice={isMobileDevice}
                      // Resize preview props
                      onResizePreviewStart={startResizePreview}
                      onResizePreviewUpdate={updateResizePreview}
                      onResizePreviewStop={stopResizePreview}
                    />
                  ) : (
                    <ViewWidget
                      id={item.id}
                      widget={item}
                      layout={item.layout}
                      config={item.config}
                      spaceId={spaceId}
                      spaceSlug={spaceSlug}
                      spaceData={spaceData}
                      previewMode={activeLayout}
                      handleWidgetUpdate={handleWidgetUpdate}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Layout items and placeholders */}
          {items.map((item) => {
            const pos = positions[item.id] || {
              left: 0,
              top: 0,
              width: 200,
              height: 200,
            };

            // Ensure all position values are valid numbers to prevent NaN errors
            let safePos = {
              left: isNaN(pos.left) ? 0 : pos.left,
              top: isNaN(pos.top) ? 0 : pos.top,
              width: isNaN(pos.width) || pos.width <= 0 ? 200 : pos.width,
              height: isNaN(pos.height) || pos.height <= 0 ? 200 : pos.height,
            };

            // Check for resize preview and apply displaced positions
            const displacedPosition = getDisplacedPosition(item.id);
            const currentResizePreviewInfo = getResizePreviewInfo();

            // Apply displaced position during resize preview
            if (displacedPosition) {
              const { columnWidth, unitHeight } = calculateGridUnitDimensions();
              safePos = {
                ...safePos,
                left:
                  displacedPosition.startColumn * (columnWidth + GAP_PIXELS),
                top: displacedPosition.position * (unitHeight + GAP_PIXELS),
              };
            }

            // If this is the widget being resized, keep original position but indicate preview state
            const isBeingResized =
              currentResizePreviewInfo &&
              item.id === currentResizePreviewInfo.widgetId;
            const isDisplacedInPreview = displacedPosition !== null;

            const isDragging = draggedItemId === item.id;
            const isOver =
              potentialDropArea?.type === "widget" &&
              potentialDropArea?.startColumn ===
                item.layout[activeLayout]?.startColumn &&
              potentialDropArea?.position ===
                item.layout[activeLayout]?.position;
            const isLocked = item.layout?.settings?.locked;

            if (isDragging) {
              // Show placeholder for the original position
              return (
                <div
                  key={`placeholder-${item.id}`}
                  className="absolute pointer-events-none"
                  style={{
                    transform: `translate3d(${safePos.left}px, ${safePos.top}px, 0)`,
                    width: safePos.width,
                    height: safePos.height,
                    background: isLightBackground
                      ? "rgba(0, 0, 0, 0.3)"
                      : "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(20px)",
                    WebkitBackdropFilter: "blur(20px)",
                    border: "none",
                    borderRadius: "1.5rem",
                    transition:
                      "transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                    zIndex: 1,
                    boxShadow: isLightBackground
                      ? "0 8px 32px 0 rgba(96, 165, 250, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.5)"
                      : "0 8px 32px 0 rgba(96, 165, 250, 0.3), inset 0 0 0 1px rgba(255, 255, 255, 0.2)",
                  }}
                />
              );
            }

            return (
              <div
                key={item.id}
                id={`widget-${item.id}`}
                data-widget-id={item.id}
                data-locked={isLocked ? "true" : "false"}
                className={`absolute ${isEditing ? "group" : ""}`}
                style={{
                  transform: widgetsFlyOff
                    ? calculateFlyOffTransform(item.id, safePos)
                    : `translate3d(${safePos.left}px, ${safePos.top}px, 0)`,
                  width: safePos.width,
                  height: safePos.height,
                  transition: isDragging
                    ? "none"
                    : widgetsFlyOff
                      ? "transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.8s ease-in-out"
                      : isDisplacedInPreview
                        ? "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease-in-out"
                        : "transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease-in-out, scale 0.3s ease-in-out",
                  opacity: widgetsFlyOff
                    ? 0
                    : isOver
                      ? isLightBackground
                        ? 0.6
                        : 0.5
                      : isDisplacedInPreview
                        ? 0.7
                        : 1,
                  scale: isOver ? 0.98 : 1,
                  zIndex: isDragging
                    ? 10
                    : isBeingResized
                      ? 8
                      : isDisplacedInPreview
                        ? 3
                        : 2,
                  willChange: "transform, opacity, scale",
                  transformOrigin: "center center",
                  background: "transparent",
                  position: "absolute",
                  filter: isDisplacedInPreview ? "brightness(0.9)" : "none",
                }}
              >
                {/* Red overlay for locked widgets during drag operations */}
                {isLocked && draggedItemId && (
                  <div
                    className="absolute inset-0 rounded-3xl pointer-events-none flex items-center justify-center"
                    style={{
                      backgroundColor: "rgba(242, 95, 76, 0.35)",
                      border: "3px solid rgba(242, 95, 76, 0.7)",
                      zIndex: 10,
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <div
                      className="flex items-center justify-center"
                      style={{
                        backgroundColor: "rgba(242, 95, 76, 0.5)",
                        padding: "0.5rem",
                        borderRadius: "0.5rem",
                        border: isLightBackground
                          ? "2px solid rgba(0, 0, 0, 0.7)"
                          : "2px solid rgba(255, 255, 255, 0.7)",
                        boxShadow: isLightBackground
                          ? "0 0 10px rgba(0, 0, 0, 0.4)"
                          : "0 0 10px rgba(0, 0, 0, 0.5)",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faLock}
                        style={{ fontSize: "24px" }}
                        className={
                          isLightBackground ? "text-black" : "text-white"
                        }
                      />
                    </div>
                  </div>
                )}

                {isEditing ? (
                  <EditableWidget
                    id={item.id}
                    widget={item}
                    layout={item.layout}
                    config={item.config}
                    isEditing={isEditing}
                    spaceId={spaceId}
                    spaceSlug={spaceSlug}
                    onSaveStatusChange={onSaveStatusChange}
                    handleWidgetUpdate={handleWidgetUpdate}
                    onDelete={handleWidgetDelete}
                    spaceData={spaceData}
                    previewMode={activeLayout}
                    totalColumns={PREVIEW_SETTINGS[activeLayout].totalColumns}
                    handleMouseDown={handleMouseDown}
                    allWidgets={items}
                    queueAction={queueAction}
                    onOpenSettings={handleOpenSettings}
                    onCloseSettings={handleCloseSettings}
                    // Mobile widget selection props
                    selectedMobileWidgetId={selectedMobileWidgetId}
                    isMobileDevice={isMobileDevice}
                    // Resize preview props
                    onResizePreviewStart={startResizePreview}
                    onResizePreviewUpdate={updateResizePreview}
                    onResizePreviewStop={stopResizePreview}
                  />
                ) : (
                  <ViewWidget
                    id={item.id}
                    widget={item}
                    layout={item.layout}
                    config={item.config}
                    spaceId={spaceId}
                    spaceSlug={spaceSlug}
                    spaceData={spaceData}
                    previewMode={activeLayout}
                    handleWidgetUpdate={handleWidgetUpdate}
                  />
                )}

                {/* Mobile widget selection overlay */}
                {isEditing &&
                  isMobileDevice &&
                  mobileEditor.renderWidgetSelectionOverlay(item)}
              </div>
            );
          })}

          {/* Resize preview outlines */}
          {isResizePreviewActive() &&
            (() => {
              const previewInfo = getResizePreviewInfo();
              if (!previewInfo) return null;

              const { columnWidth, unitHeight } = calculateGridUnitDimensions();

              return (
                <>
                  {/* Main resize preview outline */}
                  <div
                    className="absolute pointer-events-none"
                    style={{
                      transform: `translate3d(${
                        previewInfo.resizeInfo.startColumn *
                        (columnWidth + GAP_PIXELS)
                      }px, ${
                        previewInfo.resizeInfo.position *
                        (unitHeight + GAP_PIXELS)
                      }px, 0)`,
                      width:
                        previewInfo.newColumns * columnWidth +
                        (previewInfo.newColumns - 1) * GAP_PIXELS,
                      height:
                        previewInfo.newColumnHeight * unitHeight +
                        (previewInfo.newColumnHeight - 1) * GAP_PIXELS,
                      background: isModifierPressed
                        ? isLightBackground
                          ? "rgba(0, 0, 0, 0.4)"
                          : "rgba(255, 255, 255, 0.2)"
                        : isLightBackground
                          ? "rgba(0, 0, 0, 0.25)"
                          : "rgba(255, 255, 255, 0.15)",
                      backdropFilter: "blur(2px)",
                      WebkitBackdropFilter: "blur(2px)",
                      border: "none",
                      borderRadius: "1.5rem",
                      transition:
                        "transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                      zIndex: (() => {
                        // Find the current widget to get its actual current dimensions
                        const currentWidget = items.find(
                          (item) => item.id === previewInfo.widgetId
                        );
                        if (!currentWidget) return 1;

                        const currentColumns =
                          currentWidget.layout[activeLayout]?.columns || 4;
                        const currentHeight =
                          currentWidget.layout[activeLayout]?.columnHeight || 4;
                        const currentSize = currentColumns * currentHeight;
                        const newSize =
                          previewInfo.newColumns * previewInfo.newColumnHeight;

                        return newSize < currentSize ? 10 : 1;
                      })(),
                      boxShadow: isModifierPressed
                        ? isLightBackground
                          ? "0 8px 32px 0 rgba(96, 165, 250, 0.25), inset 0 0 0 1px rgba(255, 255, 255, 0.6)"
                          : "0 8px 32px 0 rgba(96, 165, 250, 0.35), inset 0 0 0 1px rgba(255, 255, 255, 0.3)"
                        : isLightBackground
                          ? "0 4px 16px 0 rgba(96, 165, 250, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.4)"
                          : "0 4px 16px 0 rgba(96, 165, 250, 0.2), inset 0 0 0 1px rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </>
              );
            })()}

          {/* Dragged widget overlay */}
          {draggedItemId && dragPosition && (
            <div
              className="fixed pointer-events-none"
              style={{
                left: dragPosition.x,
                top: dragPosition.y,
                width: dragPosition.width,
                height: dragPosition.height,
                zIndex: 1000,
                transform: `rotate(${dragPosition.rotation || 0}deg)`,
                transformOrigin: "center center",
                transition: isDropAnimating
                  ? "left 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), top 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
                  : "transform 0.05s ease-out", // Smooth drop animation
                boxShadow: isLightBackground
                  ? "0 10px 25px rgba(0, 0, 0, 0.15)"
                  : "0 10px 25px rgba(0, 0, 0, 0.3)", // Enhanced shadow for depth
                willChange: "transform, left, top", // Performance optimization
              }}
            >
              {/* Use ViewWidget for dragged item to avoid edit controls during drag */}
              <ViewWidget
                id={draggedItemId}
                widget={items.find((item) => item.id === draggedItemId)}
                layout={items.find((item) => item.id === draggedItemId)?.layout}
                config={items.find((item) => item.id === draggedItemId)?.config}
                spaceId={spaceId}
                spaceSlug={spaceSlug}
                spaceData={spaceData}
                previewMode={activeLayout}
                handleWidgetUpdate={handleWidgetUpdate}
              />
            </div>
          )}
        </div>

        {/* Render widget settings portal outside of any widget containers */}
        {widgetWithOpenSettings && (
          <WidgetSettingsPortal
            showSettings={true}
            isMobileDevice={isMobileDevice}
            widget={widgetWithOpenSettings.widget}
            config={widgetWithOpenSettings.widget.config}
            layout={widgetWithOpenSettings.widget.layout}
            settingsType={widgetWithOpenSettings.settingsType}
            onLayoutChange={(newLayout, batchUpdate) => {
              // Handle layout changes for the widget with open settings
              if (batchUpdate && batchUpdate.widgets) {
                // Handle batch updates
                handleWidgetUpdate(null, batchUpdate);
              } else {
                // Handle single widget update - properly merge layouts
                const currentLayout = widgetWithOpenSettings.widget.layout;
                const mergedLayout = {
                  ...currentLayout,
                  ...newLayout,
                  // Ensure settings are properly merged, not replaced
                  settings: {
                    ...(currentLayout.settings || {}),
                    ...(newLayout.settings || {}),
                  },
                };

                const updateData = {
                  layout: mergedLayout,
                };

                // Pass through the isRealTimeUpdate flag if present
                if (batchUpdate && batchUpdate.isRealTimeUpdate) {
                  updateData.isRealTimeUpdate = true;
                  updateData.skipHistory = true;
                  updateData.triggerAutosave = false;
                }

                handleWidgetUpdate(
                  widgetWithOpenSettings.widget.id,
                  updateData
                );
              }
            }}
            onConfigChange={(newConfig, batchUpdate) => {
              // Handle config changes for the widget with open settings
              if (batchUpdate && batchUpdate.widgets) {
                // Handle batch updates
                handleWidgetUpdate(null, batchUpdate);
              } else {
                // Handle single widget update
                handleWidgetUpdate(widgetWithOpenSettings.widget.id, {
                  config: newConfig,
                });
              }
            }}
            onClose={handleCloseSettings}
            previewMode={activeLayout}
            onLockToggle={() => {
              const newLayout = {
                ...widgetWithOpenSettings.widget.layout,
                settings: {
                  ...widgetWithOpenSettings.widget.layout.settings,
                  locked:
                    !widgetWithOpenSettings.widget.layout.settings?.locked,
                },
              };
              handleWidgetUpdate(widgetWithOpenSettings.widget.id, {
                layout: newLayout,
              });
            }}
            spaceData={spaceData}
            allWidgets={items}
          />
        )}

        {/* Mobile control bar */}
        {isEditing && isMobileDevice && mobileEditor.renderMobileControlBar()}

        {/* Mobile settings drawer */}
        {isEditing &&
          isMobileDevice &&
          mobileEditor.renderMobileSettingsDrawer()}
      </div>
    );
  }
);

MasonryLayout.displayName = "MasonryLayout";

export default MasonryLayout;
